import React from "react";
import ReactMarkdown from "react-markdown";

interface SafeMarkdownProps {
  children: string;
  baseUrl?: string;
}

const resolveImageSrc = (
  src?: string,
  baseUrl?: string,
): string | undefined => {
  if (!src || !baseUrl) {
    return src;
  }

  try {
    const resolved = new URL(src, baseUrl);
    return resolved.protocol === "https:" || resolved.protocol === "http:"
      ? resolved.href
      : src;
  } catch {
    return src;
  }
};

export const SafeMarkdown: React.FC<SafeMarkdownProps> = ({
  children,
  baseUrl,
}) => (
  <ReactMarkdown
    components={{
      a: ({ children: linkChildren, href, title }) => (
        <a
          href={href}
          title={title}
          target="_blank"
          rel="noopener noreferrer"
          className="text-highlight underline transition-opacity hover:opacity-80"
        >
          {linkChildren}
        </a>
      ),
      blockquote: ({ children: quoteChildren }) => (
        <blockquote className="my-4 border-l-4 border-gray-300 pl-4 italic text-gray-200">
          {quoteChildren}
        </blockquote>
      ),
      code: ({ children: codeChildren, className }) => (
        <code
          className={
            className
              ? `${className} text-gray-200`
              : "rounded bg-gray-700 px-1 text-gray-200"
          }
        >
          {codeChildren}
        </code>
      ),
      h1: ({ children: headingChildren }) => (
        <h1 className="my-4 text-2xl font-bold text-white">
          {headingChildren}
        </h1>
      ),
      h2: ({ children: headingChildren }) => (
        <h2 className="my-4 text-2xl font-bold text-white">
          {headingChildren}
        </h2>
      ),
      h3: ({ children: headingChildren }) => (
        <h3 className="my-4 text-xl font-bold text-white">{headingChildren}</h3>
      ),
      h4: ({ children: headingChildren }) => (
        <h4 className="my-4 text-lg font-bold text-white">{headingChildren}</h4>
      ),
      hr: () => <hr className="my-8 border-gray-600" />,
      img: ({ src, alt, title }) => (
        <img
          src={resolveImageSrc(src, baseUrl)}
          alt={alt || ""}
          title={title}
          className="my-4 h-auto max-w-full rounded-lg"
          loading="lazy"
        />
      ),
      ol: ({ children: listChildren }) => (
        <ol className="my-4 list-decimal pl-6">{listChildren}</ol>
      ),
      p: ({ children: paragraphChildren }) => (
        <p className="my-4 text-gray-200">{paragraphChildren}</p>
      ),
      pre: ({ children: preChildren }) => (
        <pre className="my-4 overflow-x-auto rounded-lg bg-gray-800 p-4">
          {preChildren}
        </pre>
      ),
      strong: ({ children: strongChildren }) => (
        <strong className="font-bold text-gray-200">{strongChildren}</strong>
      ),
      table: ({ children: tableChildren }) => (
        <div className="my-4 overflow-x-auto">
          <table className="min-w-full border-collapse rounded-md border border-white">
            {tableChildren}
          </table>
        </div>
      ),
      td: ({ children: cellChildren }) => (
        <td className="border-b border-white px-4 py-2 text-gray-200">
          {cellChildren}
        </td>
      ),
      th: ({ children: cellChildren }) => (
        <th className="border-b border-white bg-zinc-900 px-4 py-2 text-left text-gray-200">
          {cellChildren}
        </th>
      ),
      ul: ({ children: listChildren }) => (
        <ul className="my-4 list-disc pl-6">{listChildren}</ul>
      ),
    }}
  >
    {children}
  </ReactMarkdown>
);
